import React, { useState } from 'react';

// ============ HOMEPAGE ============
const HomePage = ({ onCta, onAudit, goTo, tweaks }) => {
  return (
    <>
      <section className="hero">
        <div className="container">
          <div className="eyebrow accent">Monitored Growth · A Digital Rodeo Group brand</div>
          <h1 style={{marginTop:24}}>
            You built a security company. Now build the <span className="accent">engine</span> that makes it grow, or sell, on your terms.
          </h1>
          <p className="hero-sub">
            Marketing built for $1M–$10M security dealers. Not home services. Not B2B SaaS. Security. We speak alarm panels, RMR, attrition, and exit multiples, because we ran marketing inside the industry before we built this firm to fix it.
          </p>

          <div className="seg-select">
            <div className="label-row">
              <span className="label">Which one are you?</span>
              <span className="label" style={{color:"var(--mute-2)"}}>Hover to see pain markers</span>
            </div>
            <div className="seg-grid">
              <button className="seg-card" onClick={()=>goTo("residential")}>
                <div>
                  <div className="seg-num">01 · Residential</div>
                  <h3>I sell mostly to homeowners.</h3>
                </div>
                <ul className="seg-pains">
                  <li>SimpliSafe and Ring eating your front-funnel</li>
                  <li>Attrition stuck above 12%</li>
                  <li>Google Ads outbid by ADT and Vivint</li>
                </ul>
                <div className="seg-arrow"><span>Residential</span><span>→</span></div>
              </button>
              <button className="seg-card" onClick={()=>goTo("commercial")}>
                <div>
                  <div className="seg-num">02 · Commercial</div>
                  <h3>I sell mostly to businesses.</h3>
                </div>
                <ul className="seg-pains">
                  <li>Pipeline depends on relationships and trade shows</li>
                  <li>Website hasn't been touched since 2019</li>
                  <li>"We don't really do marketing" pipeline lumpy</li>
                </ul>
                <div className="seg-arrow"><span>Commercial</span><span>→</span></div>
              </button>
              <button className="seg-card" onClick={()=>goTo("mixed")}>
                <div>
                  <div className="seg-num">03 · Mixed</div>
                  <h3>I do both, and the mix matters.</h3>
                </div>
                <ul className="seg-pains">
                  <li>One brand trying to talk to two buyers</li>
                  <li>Budget split too thin to move either side</li>
                  <li>Rotating to commercial without losing residential RMR</li>
                </ul>
                <div className="seg-arrow"><span>Mixed</span><span>→</span></div>
              </button>
              <button className="seg-card featured" onClick={()=>goTo("exit")}>
                <div>
                  <div className="seg-num">04 · Exit Accelerator</div>
                  <h3>I'm planning to sell my company in the next 3 years.</h3>
                </div>
                <ul className="seg-pains">
                  <li>Multiple expansion is a 24-month build</li>
                  <li>Buyers diligence marketing like financials</li>
                  <li>Application-only program</li>
                </ul>
                <div className="seg-arrow"><span>Exit Accelerator</span><span>→</span></div>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="section tight testimonial-section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow accent">What dealers say</div>
          </div>
          <figure className="testimonial">
            <div className="testimonial-stars" role="img" aria-label="Rated 5 out of 5 stars">★★★★★</div>
            <blockquote className="testimonial-quote">
              "Steve has been amazing to work with. He's got our website performing top notch, and best of all we've <span className="amp">started closing more deals almost immediately</span> once utilizing Digital Rodeo's services. Strongly recommend to anyone needing website management and marketing help for their business."
            </blockquote>
            <figcaption className="testimonial-by">
              <span className="testimonial-name">Chad Jones</span>
              <span className="testimonial-role">Owner, Caprock Rentals · mobile security camera rentals, Central Texas</span>
              <span className="testimonial-source">Verified Google review</span>
            </figcaption>
          </figure>
          <div style={{marginTop:32}}>
            <button className="btn btn-line" onClick={()=>goTo("case-studies/caprock-rentals")}>Read the Caprock Rentals case study <span className="arrow">→</span></button>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Three books, three failure modes</div>
            <h2 style={{marginTop:18}}>You already know which column is yours.</h2>
          </div>
          <div className="pain-cols">
            <div className="pain-col">
              <span className="label">Residential</span>
              <h3>Mostly residential? <em>You're feeling:</em></h3>
              <ul>
                <li>SimpliSafe and Ring eating the front of your funnel before homeowners ever search your name</li>
                <li>Attrition holding stubbornly above 12% no matter how many new subs you add</li>
                <li>Google Ads costs climbing while ADT and Vivint floor-bid every keyword</li>
              </ul>
            </div>
            <div className="pain-col">
              <span className="label">Mixed</span>
              <h3>Mixed book? <em>You're feeling:</em></h3>
              <ul>
                <li>One website trying to talk to homeowners and facility managers at the same time</li>
                <li>A budget that gets split too thin to move the needle on either side</li>
                <li>The need to rotate toward commercial without losing the residential RMR base</li>
              </ul>
            </div>
            <div className="pain-col">
              <span className="label">Commercial</span>
              <h3>Mostly commercial? <em>You're feeling:</em></h3>
              <ul>
                <li>Bid pipeline depending entirely on relationships and trade-show presence</li>
                <li>A website that hasn't been touched since 2019 and looks it</li>
                <li>"We don't really do marketing," and the project pipeline is lumpy because of it</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="split-2">
            <div>
              <div className="eyebrow">Why Monitored Growth</div>
              <h2 style={{marginTop:18}}>Most marketing agencies treat your business like a roofer with cameras attached.</h2>
            </div>
            <div className="body-prose">
              <p>You don't sell roofs. You sell recurring monthly revenue, multiplied at sale by 30 to 46 times. Different math, different buyer behavior, different competitive set. ADT, SimpliSafe, Pye-Barker, and Alarm.com aren't problems a generalist agency understands, and they're the four largest variables in your business.</p>
              <p>We built Monitored Growth to fix that. We measure every dollar we spend against the metrics buyers pay for: attrition, organic lead share, RMR creation cost, brand equity that survives a logo change. Not lead volume, not vanity traffic. The numbers that move enterprise value.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow accent">AI search visibility</div>
            <h2 style={{marginTop:18,maxWidth:"26ch"}}>When a homeowner asks AI for a security company they can trust, your name should be the answer.</h2>
          </div>
          <div className="split-2" style={{marginTop:8}}>
            <div className="body-prose">
              <p>The buying journey changed while you were closing accounts. A homeowner deciding between you and the national brand down the street used to start on Google. Now plenty of them ask ChatGPT or Gemini: "who is a reputable alarm company in my area?" The AI hands back two or three names. If yours isn't one of them, you never knew the lead existed.</p>
              <p>The national brands are already optimizing for this. Most independent dealers are not. That gap is your opening, and it's closing fast. I ran marketing inside a national security brand for years. I know how they win the search results, and how a dealer doing $1M to $10M takes that ground back in their own market without a national budget.</p>
            </div>
            <div className="callout">
              <span className="label">What this looks like for your shop</span>
              <p>A Google profile the AI trusts, a review engine that signals you're the safe choice, and a website the answer engines can read and quote. When AI recommends a local dealer, it recommends you.</p>
            </div>
          </div>
          <div style={{marginTop:32,display:"flex",gap:14,flexWrap:"wrap",alignItems:"center"}}>
            <button className="btn btn-primary" onClick={onAudit}>Get your free AI Visibility Report <span className="arrow">→</span></button>
            <span style={{fontFamily:"var(--f-mono)",fontSize:13,letterSpacing:".02em",color:"var(--mute)"}}>See what AI says about your company today.</span>
          </div>
        </div>
      </section>

      <section className="section tight">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow accent">Outcomes that move multiples</div>
            <h2 style={{marginTop:18}}>The numbers buyers pay for.</h2>
          </div>
          <div className="outcomes">
            <div className="outcome down">
              <div className="big">3 pts</div>
              <div className="heading">Pull attrition down 3 points</div>
              <p className="desc">A $250K/month RMR book at 12% attrition vs. 9% retains <strong>$90K more in annual RMR</strong>. At a 42x multiple, that's <strong>$3.78M in enterprise value preserved</strong>, without adding a single new customer.</p>
            </div>
            <div className="outcome">
              <div className="big">55%</div>
              <div className="heading">Move organic share above 55%</div>
              <p className="desc">Buyers underwrite organic-lead engines at premium multiples and discount paid-lead-dependent books. Shifting from <strong>30% organic to 55%</strong> over 18 months is one of the highest-leverage moves an owner can make before sale.</p>
            </div>
            <div className="outcome">
              <div className="big">48 hrs</div>
              <div className="heading" style={{}}>Document a marketing function that survives the founder</div>
              <p className="desc">SOPs, vendor contracts, attribution data, and brand assets, built so a buyer can diligence the program in <strong>48 hours</strong> and transition it in 90 days. Most $5M dealers cannot produce this. The ones who can transact at the top of their multiple band.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="proof-strip">
        <div className="container proof-strip-inner">
          <p className="proof-text">
            Built by a security industry veteran. <strong>Marketing Director at the 5th-largest U.S. home security company</strong> before founding Monitored Growth. We've sat in your seat, managing the RMR, the attrition reports, the lead vendors, the renewal calls, and the cellular sunset.
          </p>
          <div className="proof-portrait">
            <img src="/assets/founder.jpg" alt="Founder portrait"/>
          </div>
        </div>
      </section>

      <FinalCTA onCta={onCta} headline={<>The dealers winning in 2026 aren't running a different business. They're running the same business with a <span className="amp">different operating system</span> underneath it.</>}/>
    </>
  );
};

// ============ Segment page (residential / mixed / commercial) ============
const SegmentPage = ({ data, onCta, goTo }) => {
  const t = data;
  return (
    <>
      <section className="hero">
        <div className="container">
          <div className="split-2">
            <div>
              <div className="eyebrow">{t.eyebrow}</div>
              <h1 style={{marginTop:18,fontSize:"clamp(36px,4.6vw,64px)"}}>{t.headline}</h1>
            </div>
            <div className="body-prose" style={{marginTop:24}}>
              {t.sub.map((p,i)=><p key={i}>{p}</p>)}
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-narrow">
          <div className="eyebrow">Pain mirror</div>
          <div className="body-prose" style={{marginTop:18}}>
            {t.painMirror.map((p,i)=><p key={i}>{p}</p>)}
          </div>

          <div className="callout">
            <span className="label">{t.callout.title}</span>
            {t.callout.body.map((p,i)=><p key={i} dangerouslySetInnerHTML={{__html:p}}/>)}
          </div>

          {t.caseStudy && (
            <div className="case-proof">
              <span className="label">Proof</span>
              <p>{t.caseStudy.line}</p>
              <button className="btn btn-line" onClick={()=>goTo(t.caseStudy.id)}>{t.caseStudy.cta} <span className="arrow">→</span></button>
            </div>
          )}
        </div>
      </section>

      {t.uniqueElement && (
        <section className="section tight">
          <div className="container">
            <div className="section-head">
              <div className="eyebrow accent">{t.uniqueElement.eyebrow}</div>
              <h2 style={{marginTop:18}}>{t.uniqueElement.title}</h2>
              <p className="lede" style={{marginTop:18}}>{t.uniqueElement.lede}</p>
            </div>
            {t.uniqueElement.render}
          </div>
        </section>
      )}

      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Engagement tiers</div>
            <h2 style={{marginTop:18}}>{t.tiersTitle}</h2>
            <p className="lede" style={{marginTop:18}}>{t.tiersLede}</p>
          </div>
          <div className="tiers">
            {t.tiers.map((tier,i)=>(
              <div className={`tier ${tier.featured?"featured":""}`} key={i}>
                {tier.featured && <span className="tier-popular">Most common</span>}
                <div className="tier-name">{tier.name}</div>
                <div className="tier-price">{tier.price} <span className="per">/ mo</span></div>
                <div className="tier-tag">{tier.tag}</div>
                <ul>
                  {tier.bullets.map((b,j)=><li key={j}>{b}</li>)}
                </ul>
                <div className="tier-break">Break-even: <span className="num">{tier.breakEven}</span></div>
                <button className="btn btn-ghost" onClick={onCta}>Book a call <span className="arrow">→</span></button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section tight">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Field guide</div>
            <h2 style={{marginTop:18,fontSize:"clamp(28px,3vw,40px)",maxWidth:"24ch"}}>{t.guide.title}</h2>
          </div>
          <div className="guide-promo">
            <div className="guide-cover">
              <div>
                <div className="cov-label">{t.guide.coverLabel}</div>
              </div>
              <div className="cov-stripe"></div>
              <div className="cov-title">{t.guide.coverTitle}</div>
              <div className="cov-foot">Monitored Growth · Field Guide No. {t.guide.no}</div>
            </div>
            <div>
              <p className="body-prose" style={{marginBottom:24}}>{t.guide.body}</p>
              <button className="btn btn-primary" onClick={onCta}>Download the guide <span className="arrow">→</span></button>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA onCta={onCta} headline={<>Book a 30-minute call. We'll review your current attrition, your current acquisition mix, and tell you which tier fits, <span className="amp">including whether you should be talking to us at all.</span></>}/>

      <div className="exit-strip">
        <div className="container exit-strip-inner">
          <span className="lhs">Selling within 3 years?</span>
          <button onClick={()=>goTo("exit")} style={{background:"transparent",border:0,cursor:"pointer",color:"var(--orange)",fontFamily:"var(--f-mono)",fontSize:13,letterSpacing:".04em"}}>See the Exit Accelerator →</button>
        </div>
      </div>
    </>
  );
};

// ============ EXIT ACCELERATOR ============
const ExitPage = ({ onCta }) => {
  const [form,setForm] = React.useState({name:"",company:"",revenue:"",rmr:"",timeline:"",phone:"",email:"",reason:""});
  const [sent,setSent] = React.useState(false);
  const submit = (e) => {
    e.preventDefault();
    fetch("/api/lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...form, formType: "exit" }),
      keepalive: true,
    }).catch(err => console.error("exit lead post failed", err));
    setSent(true);
  };

  return (
    <>
      <section className="hero" style={{paddingTop:140,paddingBottom:80}}>
        <div className="container-narrow" style={{textAlign:"center"}}>
          <div className="eyebrow accent" style={{justifyContent:"center"}}><em style={{fontStyle:"italic",fontFamily:"var(--f-serif)",letterSpacing:0,textTransform:"none",fontSize:14,color:"var(--orange)"}}>Exit Accelerator</em></div>
          <h1 style={{marginTop:28,fontSize:"clamp(40px,5vw,72px)"}}>
            A 24-month marketing program built specifically to <span className="accent">expand your sale multiple.</span>
          </h1>
          <p className="lede" style={{marginTop:28,marginLeft:"auto",marginRight:"auto"}}>
            For owners of $2M–$10M security companies preparing to sell within the next 36 months. Application-only.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-narrow">
          <div className="eyebrow">The premise</div>
          <div className="body-prose" style={{marginTop:24}}>
            <p>Most security company owners start preparing for sale 12 to 18 months before close. By then, the variables that move multiples are baked: attrition, organic lead share, brand equity, marketing operations documentation. You're negotiating around what you have, not building what you wish you'd built.</p>
            <p>The owners who transact at the top of their multiple band started 24 to 36 months earlier. They didn't run a "sale prep" program. They ran a marketing program that happened to make them irresistible to buyers.</p>
            <p><em style={{fontFamily:"var(--f-serif)",color:"var(--orange)"}}>The Exit Accelerator is that program.</em></p>
          </div>
        </div>
      </section>

      <section className="section tight">
        <div className="container-narrow">
          <div className="eyebrow accent">The math buyers pay</div>
          <h2 style={{marginTop:18,marginBottom:22}}>Mike Barnes' 2024 transaction data, ESX 2025 presentation.</h2>
          <table className="data-table">
            <thead><tr><th>Monthly RMR band</th><th>Multiple</th></tr></thead>
            <tbody>
              <tr><td>Under $50K</td><td>~36x</td></tr>
              <tr><td>$50K – $250K</td><td>~40x</td></tr>
              <tr><td>Over $500K</td><td>~46x</td></tr>
            </tbody>
          </table>
          <p className="body-prose" style={{marginTop:28}}>
            Inside each size band, <strong style={{color:"var(--cream)"}}>above-average books transact at 38x to 44x; below-average books drop to 28x to 34x</strong>.
          </p>
          <div className="callout" style={{marginTop:24}}>
            <p>A $5M dealer at $250K monthly RMR transacting at 42x earns <strong>$10.5M</strong>. The same dealer at 36x earns <strong>$9M</strong>. Same business. <strong>$1.5M difference.</strong> The variables that move you between those numbers are the ones a marketing program controls.</p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Four pillars</div>
            <h2 style={{marginTop:18}}>The variables a 24-month program controls.</h2>
          </div>
          <div className="pillars">
            {[
              {n:"01",t:"Attrition reduction",p:["The single highest-leverage variable. National residential gross attrition averages 12 to 13%. Best-in-class operators run 7 to 10%.","We run: onboarding sequence rebuild for new subs, quarterly health-check campaigns for existing subs, save-desk SOPs, win-back program for cancelled accounts."],target:"Drop gross attrition 200–400 bps over 24 months. On a $250K/month RMR book, three points retained = $90K annual RMR = $3.78M in enterprise value preserved."},
              {n:"02",t:"Organic lead engine",p:["Buyers discount books built on paid leads. They pay premiums for organic-acquisition books.","We run: AI search visibility across the map, search results, and AI answers, answer-first content, Google Business Profile across all service areas, a review engine, and owned email list growth."],target:"Organic share rises from 25–35% to 55–65%."},
              {n:"03",t:"Brand and authority",p:["Brand equity transfers in a sale. Reputation, reviews, search visibility, and category authority remain after a logo change.","We run: refreshed brand identity, local and trade PR, owner thought leadership, video portfolio, customer success story library."],target:"Diligence teams find a credible, consistent brand at every research touchpoint."},
              {n:"04",t:"Marketing operations documentation",p:["Buyers run diligence on marketing the same way they run it on financials. Most $5M dealers cannot produce a defensible CAC by channel.","We build: buyer-ready marketing data room, SOPs for every recurring campaign, vendor contract consolidation, attribution architecture, 90-day transition plan."],target:"By month 18, your marketing function can be diligenced in 48 hours and transitioned in 90 days."}
            ].map((p,i)=>(
              <div className="pillar" key={i}>
                <span className="pillar-num">PILLAR {p.n}</span>
                <h3>{p.t}</h3>
                {p.p.map((x,j)=><p key={j}>{x}</p>)}
                <div className="target">
                  <span className="label">Target outcome</span>
                  <strong>{p.target}</strong>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section tight">
        <div className="container-narrow">
          <div className="eyebrow">Engagement structure</div>
          <h2 style={{marginTop:18,marginBottom:32}}>24-month engagement, two pricing components.</h2>
          <div className="callout">
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:32,fontSize:14.5,fontFamily:"var(--f-mono)"}}>
              <div>
                <div className="label" style={{color:"var(--mute)",marginBottom:8}}>Base fee</div>
                <div style={{color:"var(--cream)",fontSize:18,marginBottom:6}}>$12K – $18K / mo</div>
                <div style={{color:"var(--cream-2)",fontSize:13,fontWeight:300,fontFamily:"var(--f-sans)",lineHeight:1.5}}>Scaled to dealer size and book complexity. Total over 24 months: $288K to $432K.</div>
              </div>
              <div>
                <div className="label" style={{color:"var(--mute)",marginBottom:8}}>Optional success fee</div>
                <div style={{color:"var(--cream)",fontSize:18,marginBottom:6}}>1 – 2% of incremental sale price</div>
                <div style={{color:"var(--cream-2)",fontSize:13,fontWeight:300,fontFamily:"var(--f-sans)",lineHeight:1.5}}>Above an agreed-upon baseline valuation. Capped.</div>
              </div>
            </div>
          </div>
          <p className="body-prose" style={{marginTop:24}}>
            <strong style={{color:"var(--cream)"}}>The math:</strong> on a $5M dealer transacting at 42x vs. 36x, multiple expansion alone is worth $1.5M+. That recovers the base fee 4 to 5 times over. The success fee functions as alignment, not main economics.
          </p>
        </div>
      </section>

      <section className="section tight">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Fit</div>
            <h2 style={{marginTop:18}}>Who this is for. Who it isn't.</h2>
          </div>
          <div className="who-for">
            <div className="who-for-col">
              <h4>Who this is for</h4>
              <ul>
                <li>Owners of $2M–$10M security companies, 24–48 months from sale</li>
                <li>Owners who've taken at least one buyer call</li>
                <li>Owners noticing a peer's recent transaction and wondering about their own number</li>
                <li>Owners tired enough to want to build something rather than get bought from fatigue</li>
              </ul>
              <p className="footnote">If two or more of these are true, you're closer to sale than you've let on.</p>
            </div>
            <div className="who-for-col no">
              <h4>Who this is not for</h4>
              <ul>
                <li>Dealers under $2M revenue. The math doesn't work yet.</li>
                <li>Owners more than 48 months from sale. Run a standard tier program; revisit when closer.</li>
                <li>Owners who want sale prep without the underlying marketing rebuild. Buyers see through cosmetic prep.</li>
                <li>Owners whose book is fundamentally distressed. We're a multiplier, not a turnaround.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section tight">
        <div className="container-narrow">
          <div className="eyebrow accent">How to apply</div>
          <h2 style={{marginTop:18,marginBottom:32}}>Four steps to the Multiple Expansion Analysis.</h2>
          <div className="process">
            {[
              {n:"01",t:"Submit application",d:"10 minutes. Form below."},
              {n:"02",t:"Strategy call",d:"60 minutes with a senior strategist."},
              {n:"03",t:"Multiple Expansion Analysis",d:"Delivered in 10 business days. Yours regardless of whether we engage."},
              {n:"04",t:"Engagement scoping",d:"Scoping call and contract."}
            ].map((s,i)=>(
              <div className="process-step" key={i}>
                <span className="num">{s.n}</span>
                <h4>{s.t}</h4>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-narrow">
          <div className="eyebrow accent">Application</div>
          <h2 style={{marginTop:18,marginBottom:8}}>Apply for the Multiple Expansion Analysis.</h2>
          <p className="lede" style={{marginBottom:32}}>Banker-style form. Just enough to qualify.</p>

          {sent ? (
            <div className="form-wrap form-success">
              <div className="eyebrow accent">Received</div>
              <h3 style={{marginTop:14,marginBottom:8}}>Thank you, {form.name.split(" ")[0] || "there"}.</h3>
              <p className="body-prose">A senior strategist will be in touch within two business days to schedule the 60-minute strategy call. Look for an email from <strong style={{color:"var(--cream)"}}>exit@monitoredgrowth.com</strong>.</p>
            </div>
          ) : (
            <form className="form-wrap" onSubmit={submit}>
              <div className="form-grid">
                <div className="field"><label>Name</label><input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></div>
                <div className="field"><label>Company</label><input required value={form.company} onChange={e=>setForm({...form,company:e.target.value})}/></div>
                <div className="field">
                  <label>Annual revenue</label>
                  <select required value={form.revenue} onChange={e=>setForm({...form,revenue:e.target.value})}>
                    <option value="">Select…</option>
                    <option>Under $2M</option><option>$2M – $5M</option><option>$5M – $10M</option><option>Over $10M</option>
                  </select>
                </div>
                <div className="field"><label>Approximate monthly RMR</label><input required value={form.rmr} onChange={e=>setForm({...form,rmr:e.target.value})} placeholder="$"/></div>
                <div className="field">
                  <label>Anticipated sale timeline</label>
                  <select required value={form.timeline} onChange={e=>setForm({...form,timeline:e.target.value})}>
                    <option value="">Select…</option>
                    <option>0 – 12 months</option><option>12 – 24 months</option><option>24 – 36 months</option><option>36+ months</option>
                  </select>
                </div>
                <div className="field"><label>Phone</label><input required value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/></div>
                <div className="field full"><label>Email</label><input required type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></div>
                <div className="field full"><label>Briefly, what's prompting you to consider exit timing now?</label><textarea rows="4" value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})}/></div>
              </div>
              <button className="btn btn-primary" type="submit" style={{marginTop:24}}>Apply for the Multiple Expansion Analysis <span className="arrow">→</span></button>
            </form>
          )}
        </div>
      </section>
    </>
  );
};

// ============ ABOUT ============
const AboutPage = ({ onCta }) => {
  return (
    <>
      <section className="hero">
        <div className="container">
          <div className="about-hero">
            <div>
              <div className="eyebrow">About the founder</div>
              <h1 style={{marginTop:24,fontSize:"clamp(36px,4.6vw,64px)"}}>
                25 years in marketing. Seven of them spent <span className="accent">inside the security industry itself.</span>
              </h1>
              <p className="lede" style={{marginTop:28}}>
                I built Monitored Growth because security operators deserve a marketing partner who already knows the business, not a generalist agency applying home-services tactics with a security label bolted on top.
              </p>
            </div>
            <div className="founder-portrait">
              <img src="/assets/founder.jpg" alt="Steve Rhom, founder"/>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="split-2">
            <div className="body-prose">
              <p>Before Monitored Growth, I spent seven years at the 5th-largest home security company in the country, three and a half of them as Marketing Director. Long enough to learn that most of what I'd picked up at agencies before that (funnels, attribution, lead nurture, brand) only half-applied once I was inside the operations of a real RMR business.</p>
              <p>I sat through quarterly attrition reviews. I read the cancellation reasons. I watched cellular sunsets force entire customer bases to be re-papered. I owned the lead-vendor relationships. I sat in on save-desk calls. I saw what marketing automation could do and where it stopped. And when the M&A team audited the marketing program during a sale process, I saw which numbers made buyers lean in and which ones made them flinch.</p>
              <p>That experience is the whole point of the firm. Underwriting marketing to attrition, building documentation an M&A team will accept, explaining to an operator why CAC climbed 14% the same month new-sub volume climbed 22%. That's the work, and it comes from having owned those numbers myself.</p>
              <p>Monitored Growth runs out of Tulsa, Oklahoma, as a sub-brand of Digital Rodeo Group. It's built for $1M–$10M security operators who want a partner already fluent in their business and who underwrites every dollar to the metrics buyers pay for.</p>
              <p>If you own a security company and want to talk through where you are, the calendar link below reaches me directly.</p>
              <div style={{marginTop:32}}>
                <button className="btn btn-primary" onClick={onCta}>Book a call <span className="arrow">→</span></button>
              </div>
            </div>
            <div>
              <div className="eyebrow accent">Operating philosophy</div>
              <ul className="principles" style={{paddingLeft:24,paddingRight:0,marginTop:24,listStyle:"none"}}>
                <li>Numbers over adjectives, always.</li>
                <li>Marketing is a compounding investment portfolio, not a monthly expense.</li>
                <li>The metrics buyers pay for are the only ones that matter.</li>
                <li>Niche industry expertise beats generalist marketing experience.</li>
                <li>Specific over generic, every time.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// ============ GUIDES ============
const GuidesPage = ({ onGuide }) => {
  const guides = [
    {
      no:"01", segment:"Residential", pages:"11 pages · US Letter",
      url:"guides/residential-7-reasons.pdf",
      title:"7 Reasons Local Alarm Dealers Are Losing Customers to SimpliSafe",
      body:"Where the customers went, and the seven mechanics that take them back. Includes the attrition math and Mike Barnes' multiple bands.",
      miniLabel:"Residential alarm dealers",
      miniArt: <div className="guide-mini-rule"/>,
    },
    {
      no:"02", segment:"Mixed Books", pages:"10 pages · US Letter",
      url:"guides/mix-shift-playbook.pdf",
      title:"The Mixed Dealer's Mix-Shift Playbook",
      body:"How $2M–$10M dealers rotate from 65/35 to 50/50 in 36 months without losing the residential RMR base. Four decisions, one timeline, $2.5M+ in EV created.",
      miniLabel:"Mixed res/commercial",
      miniArt: <div className="guide-mini-pair"><div className="b o"/><div className="b k"/></div>,
    },
    {
      no:"03", segment:"Commercial", pages:"12 pages · US Letter",
      url:"guides/authority-stack.pdf",
      title:"The Integrator's Authority Stack",
      body:"Five reinforcing layers that let a sub-$10M integrator out-specialize a national name: website, case studies, ABM, thought leadership, trade shows.",
      miniLabel:"Commercial integrators",
      miniArt: (
        <div className="guide-mini-stack">
          <div className="b" style={{width:48}}/>
          <div className="b" style={{width:38}}/>
          <div className="b o" style={{width:28}}/>
          <div className="b" style={{width:18}}/>
          <div className="b" style={{width:10}}/>
        </div>
      ),
    },
    {
      no:"04", segment:"Exit Prep", pages:"14 pages · US Letter",
      url:"guides/exit-multiple-multiplier.pdf",
      title:"The Exit Multiple Multiplier",
      body:"How marketing decisions in the 24 months before sale move your valuation by millions. The four variables buyers underwrite, the math, and the 24-month roadmap.",
      miniLabel:"Owners preparing to sell",
      miniArt: (
        <div className="guide-mini-bars">
          <div className="row"><span className="lbl">&lt;$50K</span><span className="bar" style={{width:"60%"}}/><span className="v">36×</span></div>
          <div className="row"><span className="lbl">$50–250K</span><span className="bar" style={{width:"74%"}}/><span className="v">40×</span></div>
          <div className="row"><span className="lbl">&gt;$500K</span><span className="bar o" style={{width:"92%"}}/><span className="v">46×</span></div>
        </div>
      ),
    },
  ];
  return (
    <>
      <section className="hero" style={{paddingBottom:60}}>
        <div className="container">
          <div className="eyebrow accent">Field guides</div>
          <h1 style={{marginTop:18,fontSize:"clamp(40px,5vw,72px)",maxWidth:"22ch"}}>
            Long-form field guides for security operators who read on weekends.
          </h1>
          <p className="lede" style={{marginTop:24}}>
            Four print-ready PDFs designed at editorial pacing. Read in twenty minutes, save for the partner who handles marketing decisions.
          </p>
        </div>
      </section>

      <section className="section no-top">
        <div className="container">
          <div className="guides-grid">
            {guides.map((g,i)=>(
              <button key={i} className="guide-card" onClick={()=>onGuide(g)}>
                <div className="guide-card-grid">
                  <div className="guide-mini">
                    <div className="guide-mini-top">
                      <span className="m">Monitored<span className="o">.</span>Growth</span>
                      <span>№ {g.no}</span>
                    </div>
                    <div className="guide-mini-label">{g.miniLabel}</div>
                    <div className="guide-mini-rule"/>
                    <div className="guide-mini-title">{g.title}</div>
                    {g.miniArt}
                    <div className="guide-mini-foot"><span>Field Guide</span><span>2026</span></div>
                  </div>
                  <div>
                    <div className="guide-num">№ {g.no} · {g.segment}</div>
                    <h3 style={{marginTop:10}}>{g.title}</h3>
                    <p style={{marginTop:10}}>{g.body}</p>
                  </div>
                </div>
                <div className="guide-meta-row">
                  <span className="pages">{g.pages}</span>
                  <span>Read <span className="arr">→</span></span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

// ============ CASE STUDY: CAPROCK RENTALS ============
// Results are client-reported. Chad Jones gave no exact figures in his
// submission, so outcomes use the ↑ heading treatment without .big numerals,
// and every claim is his, quoted. Don't add numbers here without a source.
const caprockBuilt = [
  {n:"01",t:"Service pages in buyer language",p:"Rebuilt the core pages around the words contractors actually search: mobile surveillance trailer rental, construction site security cameras, temporary jobsite surveillance."},
  {n:"02",t:"A service area that matches the territory",p:"Organized the site around the San Antonio–Austin corridor, with service-area content and internal links so each page backs up the others."},
  {n:"03",t:"A foundation AI can read",p:"Titles, descriptions, schema, and a site structure that tells Google and AI assistants what Caprock rents and where."},
  {n:"04",t:"Google Business Profile, tuned",p:"Description, services, and posts aligned with the jobs and the territory, so the map listing says what the website says."},
  {n:"05",t:"Content on a monthly cadence",p:"Supporting articles every month, each one pointing back to the pages that bring in calls."},
  {n:"06",t:"Tracking and a monthly dashboard",p:"Call and form tracking, plus a monthly dashboard that shows what's ranking, what's bringing in calls, and what comes next."}
];

const CaseStudyCaprock = ({ onCta, goTo }) => (
  <>
    <section className="hero">
      <div className="container">
        <div className="split-2">
          <div>
            <div className="eyebrow accent">Case study · Commercial</div>
            <h1 style={{marginTop:18,fontSize:"clamp(36px,4.6vw,64px)"}}>
              Built on relationships. Invisible to every contractor <span className="accent">searching for jobsite cameras.</span>
            </h1>
          </div>
          <div className="body-prose" style={{marginTop:24}}>
            <p>Caprock Rentals rents mobile surveillance camera trailers to contractors and businesses across Texas, strongest in the San Antonio–Austin corridor. The company grew the way good commercial security operators do: relationships, referrals, repeat customers, and direct sales.</p>
            <p>The website never pulled its weight. Here's what we changed, and what owner Chad Jones says happened next.</p>
          </div>
        </div>
        <dl className="case-meta">
          {[
            {l:"Company",v:"Caprock Rentals"},
            {l:"Market",v:"San Antonio–Austin corridor and Central Texas"},
            {l:"Buyers",v:"Contractors and businesses"},
            {l:"Engagement",v:"Search foundation, service pages, Google Business Profile, reporting"},
            {l:"Started",v:"April 2026"}
          ].map(m=>(
            <div key={m.l}>
              <dt className="label">{m.l}</dt>
              <dd>{m.v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>

    <section className="section tight no-top">
      <div className="container">
        <figure className="case-shot">
          <img src="/assets/case-caprock-site.webp" alt="The Caprock Rentals website shown on a laptop" width="1376" height="774"/>
        </figure>
      </div>
    </section>

    <section className="section">
      <div className="container-narrow">
        <div className="eyebrow">The situation</div>
        <h2 style={{marginTop:18,fontSize:"clamp(28px,3.4vw,44px)"}}>The website was somewhere Caprock sent customers. Nobody found Caprock through it.</h2>
        <div className="body-prose" style={{marginTop:28}}>
          <p>"Our website has never generated the amount of organic traffic or qualified leads that it should," Chad says. Contractors were searching for exactly what Caprock rents: construction site security cameras, mobile surveillance trailers, solar security camera trailers, temporary jobsite surveillance. Caprock wasn't in the results.</p>
          <p>The team had done the work themselves for years. Service pages, product pages, basic SEO fixes. "It hasn't been organized around keyword targeting, local search, service-area pages, internal linking, or a long-term content strategy," he says. "We're at the point where we want an expert to build the right foundation instead of continuing to make individual changes without knowing how they work together."</p>
        </div>
        <div className="callout">
          <span className="label">The commercial pattern</span>
          <p>A pipeline that runs on relationships and repeat business, and a website that exists because every company needs one. The buyers are already searching for what you sell. The only question is whose name they find.</p>
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container">
        <div className="section-head">
          <div className="eyebrow accent">What we built</div>
          <h2 style={{marginTop:18}}>One foundation instead of a list of fixes.</h2>
          <p className="lede">Caprock had been making individual changes for years. We organized the work into one system, built around what contractors type into Google and ask AI.</p>
        </div>
        <div className="pillars">
          {caprockBuilt.map(b=>(
            <div className="pillar" key={b.n}>
              <span className="pillar-num">{b.n}</span>
              <h3>{b.t}</h3>
              <p>{b.p}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="section tight">
      <div className="container">
        <div className="section-head">
          <div className="eyebrow accent">What changed</div>
          <h2 style={{marginTop:18}}>The website started working as a lead source.</h2>
        </div>
        <div className="outcomes">
          <div className="outcome">
            <div className="heading">Organic visibility</div>
            <p className="desc">"Our organic visibility is up, and we're getting found for more of the searches that matter to our business."</p>
          </div>
          <div className="outcome">
            <div className="heading">Calls from people who found them online</div>
            <p className="desc">"We're receiving more calls and inquiries directly from people who found us online."</p>
          </div>
          <div className="outcome">
            <div className="heading">The right buyers finding them</div>
            <p className="desc">"We need contractors and businesses searching for mobile surveillance, construction site security cameras, and temporary jobsite security to find us and call. We're seeing that happen now."</p>
          </div>
        </div>
        <p className="note" style={{marginTop:22}}>As reported by Chad Jones, owner, in Caprock's case study submission. Exact figures weren't part of it, so we haven't added any.</p>
      </div>
    </section>

    <section className="section tight testimonial-section">
      <div className="container">
        <div className="section-head">
          <div className="eyebrow accent">In his words</div>
        </div>
        <figure className="testimonial">
          <blockquote className="testimonial-quote">
            "Digital Rodeo actually gets results. We're getting found more, we're getting calls from people who found us online, and I feel like our website is <span className="amp">finally working for us</span> instead of just being something we have because every business needs a website."
          </blockquote>
          <figcaption className="testimonial-by">
            <span className="testimonial-name">Chad Jones</span>
            <span className="testimonial-role">Owner, Caprock Rentals · mobile surveillance camera rentals, Texas</span>
            <span className="testimonial-source">Case study submission · September 2026</span>
          </figcaption>
        </figure>
      </div>
    </section>

    <section className="section">
      <div className="container">
        <div className="split-2">
          <div>
            <div className="eyebrow">What surprised him</div>
            <h2 style={{marginTop:18}}>More than adding keywords.</h2>
          </div>
          <div className="body-prose">
            <p>"Probably how much goes into SEO beyond just adding keywords to a website," Chad says. "We had worked on the site ourselves for years, but having someone consistently work on the structure, content, rankings, and overall strategy has made a noticeable difference in how we're being found."</p>
            <p>That's the difference between a list of fixes and a program. It's the same approach we run for commercial security companies.</p>
            <div style={{marginTop:28}}>
              <button className="btn btn-line" onClick={()=>goTo("commercial")}>See the Commercial program <span className="arrow">→</span></button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <FinalCTA onCta={onCta} headline={<>If your pipeline runs on relationships and your website just sits there, <span className="amp">let's look at what your buyers find when they search for what you sell.</span></>}/>
  </>
);

// ============ Final CTA strip ============
const FinalCTA = ({ onCta, headline }) => (
  <section className="section tight" style={{borderTop:"1px solid var(--line)"}}>
    <div className="container-narrow">
      <h2 style={{fontSize:"clamp(28px,3.4vw,48px)",lineHeight:1.15,maxWidth:"32ch"}}>{headline}</h2>
      <div style={{marginTop:32,display:"flex",gap:14,flexWrap:"wrap"}}>
        <button className="btn btn-primary" onClick={onCta}>Book a 30-minute call <span className="arrow">→</span></button>
        <a href="#" className="btn btn-ghost" onClick={(e)=>{e.preventDefault();onCta();}}>Or email hello@monitoredgrowth.com</a>
      </div>
    </div>
  </section>
);

// ============ Footer ============
const Footer = ({ goTo }) => (
  <footer className="footer">
    <div className="container">
      <div className="footer-grid">
        <div className="footer-brand">
          <div className="footer-brand-mark">
            <img src="/assets/logo.png" alt=""/>
            <span className="name">Monitored Growth</span>
          </div>
          <p>Marketing for $1M–$10M security companies. A <a href="https://www.digitalrodeo.agency/" target="_blank" rel="noopener noreferrer" className="dr-link">Digital Rodeo Group</a> brand. Built in Tulsa, Oklahoma.</p>
        </div>
        <div className="footer-col">
          <h5>Segments</h5>
          <ul>
            <li><button onClick={()=>goTo("residential")} style={{background:"transparent",border:0,padding:0,cursor:"pointer",fontSize:14,color:"var(--mute)"}}>Residential</button></li>
            <li><button onClick={()=>goTo("mixed")} style={{background:"transparent",border:0,padding:0,cursor:"pointer",fontSize:14,color:"var(--mute)"}}>Mixed</button></li>
            <li><button onClick={()=>goTo("commercial")} style={{background:"transparent",border:0,padding:0,cursor:"pointer",fontSize:14,color:"var(--mute)"}}>Commercial</button></li>
            <li><button onClick={()=>goTo("exit")} style={{background:"transparent",border:0,padding:0,cursor:"pointer",fontSize:14,color:"var(--mute)"}}>Exit Accelerator</button></li>
          </ul>
        </div>
        <div className="footer-col">
          <h5>Resources</h5>
          <ul>
            <li><button onClick={()=>goTo("guides")} style={{background:"transparent",border:0,padding:0,cursor:"pointer",fontSize:14,color:"var(--mute)"}}>Field guides</button></li>
            <li><button onClick={()=>goTo("about")} style={{background:"transparent",border:0,padding:0,cursor:"pointer",fontSize:14,color:"var(--mute)"}}>About the founder</button></li>
            <li><button onClick={()=>goTo("case-studies/caprock-rentals")} style={{background:"transparent",border:0,padding:0,cursor:"pointer",fontSize:14,color:"var(--mute)"}}>Case study: Caprock Rentals</button></li>
          </ul>
        </div>
        <div className="footer-col">
          <h5>Company</h5>
          <ul>
            <li><a href="mailto:hello@monitoredgrowth.com">hello@monitoredgrowth.com</a></li>
            <li><a href="https://www.linkedin.com/company/digital-rodeo-group/" target="_blank" rel="noopener noreferrer">LinkedIn</a></li>
            <li><a href="https://www.digitalrodeo.agency/" target="_blank" rel="noopener noreferrer">Digital Rodeo Group</a></li>
          </ul>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© 2026 Monitored Growth · A <a href="https://www.digitalrodeo.agency/" target="_blank" rel="noopener noreferrer" className="dr-link">Digital Rodeo Group</a> brand</span>
        <span>Built in Tulsa, Oklahoma</span>
      </div>
    </div>
  </footer>
);

// ============ Segment data ============
const segmentData = {
  residential: {
    eyebrow: "Residential dealers",
    headline: <>You're not failing. You're being <span className="accent">slowly compressed.</span></>,
    sub: [
      "The playbook that built your residential book still works: door-to-door, referrals, a strong reputation in town. It just doesn't work alone anymore. ADT has State Farm and Google. SimpliSafe owns the search results. Ring owns the doorbell. Every quarter you stay reliant on the old playbook, the gap widens.",
      "We rebuild the demand engine without burning down what already works."
    ],
    painMirror: [
      "You watched ADT slap its logo on State Farm policies and Google Home. Ring and SimpliSafe ate the front of your funnel before customers ever Googled \"home security in [your city].\" Door-to-door isn't producing what it used to, and the reps who were good at it are aging out. Shared leads from Angi cost more, close lower, and churn faster than they did three years ago. Your attrition sits at 12% or higher, high enough that you're running on a treadmill, replacing customers just to stay flat. The marketing budget you do have goes to Google Ads, where you're outbid on every keyword by national brands floor-bidding the auction.",
      "You're not behind on effort. You're running the right plays for 2015 in a 2026 market."
    ],
    callout: {
      title: "The math of standing still",
      body: [
        "A $2.5M dealer with 2,500 residential subs at $45 RMR averaging 12% attrition loses 300 customers a year. Replacing them at $1,200 CAC is <strong>$360K in CAC just to stay flat</strong>, before you've added a single net new sub. Pull attrition to 9% and add 200 net new subs and you've added $108K in annual RMR. <strong>At a 38x multiple, that's $4.1M in enterprise value the day you sell.</strong>",
        "The number that matters isn't leads per month. It's enterprise value per percentage point of attrition reduction."
      ]
    },
    tiersTitle: "Three tiers. Every dealer fits one.",
    tiersLede: "Pricing scales with book size and complexity. We measure the break-even line on every tier in net new subs, not vanity metrics.",
    tiers: [
      {name:"Foundation",price:"$2.5K – $4K",tag:"Get found on the map, in search, and in AI answers. You can't grow what buyers can't find.",bullets:["AI Visibility Audit: what ChatGPT, Gemini, and Google's AI answers say about your company today","Entity foundation so AI reads you as one trusted business: Google Business Profile, consistent listings, and schema across every service area","Answer-ready service pages with FAQ blocks the AI engines can quote","Automated review generation tied to install completion","Conversion-rate-optimized site rebuild","Call tracking and attribution","Inbound lead nurture sequences"],breakEven:"~1.5 new customers per month"},
      {name:"Growth Engine",price:"$5.5K – $8.5K",featured:true,tag:"Foundation, plus active demand capture, AI tracking, and retention.",bullets:["Monthly AI visibility tracking across ChatGPT, Gemini, Perplexity, and Google AI","Review engine: requests firing after every job, every review answered, velocity held above 100","Google Ads managed by an operator who knows how to win without head-to-head bidding against ADT","Meta retargeting for warm prospects","Answer-first content targeting the questions homeowners ask AI and search","Marketing automation for nurture, win-back, renewal","Quarterly attrition review with action plan"],breakEven:"~3.5 new customers per month"},
      {name:"Market Domination",price:"$10K – $18K",tag:"Growth Engine, plus the brand and operating systems a $5M+ dealer needs to scale past the founder.",bullets:["Local PR and earned mentions that get you cited in AI answers","Competitor AI tracking: who the engines name in your market, and how to displace them","Refreshed brand identity and creative","Video content program","Account-based campaigns for builder and multi-family programs","BI dashboard pulling from Bold Group, SedonaOffice, or AlarmBiller","Dedicated marketing strategist plus creative team","Quarterly business review with the owner"],breakEven:"~7 customers per month"}
    ],
    guide: {coverLabel:"Field Guide No. 01",coverTitle:"7 Reasons Local Alarm Dealers Are Losing Customers to SimpliSafe",no:"01",title:"7 Reasons Local Alarm Dealers Are Losing Customers to SimpliSafe (and How the Best Ones Are Fighting Back in 2026)",body:"A 4,500-word field guide covering the seven dynamics eating local dealer market share, and the specific counter-moves the dealers winning in 2026 are running. Read it in 20 minutes. Save it for the partner who handles marketing decisions."}
  },
  mixed: {
    eyebrow: "Mixed dealers",
    headline: <>One website. One brand. <span className="accent">Two completely different buyers.</span></>,
    sub: [
      "The mixed dealer's hardest problem is fit, not volume. Your residential book pays the bills while you rotate toward commercial. Your marketing has to serve both, and most agencies pick a lane.",
      "We don't."
    ],
    painMirror: [
      "Your residential operation funds the company. Your commercial pipeline is where the multiple expansion lives. Marketing has to do both jobs simultaneously, on a budget that doesn't get to double when the headcount does. The website tries to talk to a homeowner shopping a $39/month plan and a facility director scoping a $180K access control deployment, and neither one feels like the page was built for them.",
      "Most agencies you've talked to pick a lane and tell you to focus. That isn't advice. It's an admission they can't run two motions in parallel."
    ],
    callout: {
      title: "The mix-shift opportunity",
      body: [
        "A $5M dealer rotating from 65/35 residential/commercial to 50/50 over 36 months is looking at <strong>$1.5M to $3M in enterprise value uplift</strong> at sale, because commercial RMR earns higher multiples and lower attrition than residential. Just from the mix shift. That's the prize."
      ]
    },
    uniqueElement: {
      eyebrow: "The dual-track model",
      title: "One brand source. Two parallel funnels.",
      lede: "We run residential and commercial as parallel programs that share brand, infrastructure, and reporting, not one campaign trying to please everyone.",
      render: (
        <div className="dual-track">
          <div className="dual-source">Monitored Growth · One brand source</div>
          <div className="dual-col">
            <h4>Residential funnel</h4>
            <ul>
              <li>Local SEO + GBP</li>
              <li>Meta retargeting</li>
              <li>Retention email program</li>
              <li>Review generation</li>
              <li>Homeowner content</li>
            </ul>
          </div>
          <div className="dual-col">
            <h4>Commercial funnel</h4>
            <ul>
              <li>LinkedIn ABM</li>
              <li>Vertical case studies</li>
              <li>Trade-show enablement</li>
              <li>Sales collateral</li>
              <li>Spec-grade content</li>
            </ul>
          </div>
        </div>
      )
    },
    tiersTitle: "Three dual-track tiers.",
    tiersLede: "Pricing reflects the work to maintain two motions in parallel without diluting either.",
    tiers: [
      {name:"Foundation · Dual-track",price:"$3.5K – $5K",tag:"Get found on the map, in search, and in AI answers, for both buyers, under one brand.",bullets:["AI Visibility Audit for both buyer types: what AI tells a homeowner and a facility manager about you today","Entity foundation so AI reads you as one trusted business: GBP across all locations, consistent listings, schema","Answer-ready site with two clear paths and FAQ blocks the AI engines can quote","Call tracking and attribution per book","Inbound nurture for both segments"],breakEven:"~2 new customers per month"},
      {name:"Growth Engine · Dual-track",price:"$7K – $11K",featured:true,tag:"Active demand capture and AI tracking across both motions.",bullets:["Monthly AI visibility tracking across ChatGPT, Gemini, Perplexity, and Google AI","Review engine across both books, every review answered","Google Ads, residential intent","LinkedIn ABM, commercial accounts","Answer-first content for both buyers","Marketing automation segmented by book","Quarterly mix-shift review"],breakEven:"~4 new customers per month"},
      {name:"Market Domination · Dual-track",price:"$13K – $20K",tag:"Full brand + ops build for the rotation toward commercial.",bullets:["Earned mentions and PR that get you cited in AI answers","Competitor AI tracking across both buyer types","Brand identity refresh","Trade-show program","Video content for both audiences","Custom commercial vertical case studies","BI dashboard with mix-shift KPIs","Dedicated team plus owner QBRs"],breakEven:"~8 customers per month"}
    ],
    guide: {coverLabel:"Field Guide No. 02",coverTitle:"The Mixed Dealer's Mix-Shift Playbook",no:"02",title:"The Mixed Dealer's Mix-Shift Playbook",body:"How to rotate from a residential-heavy book toward commercial RMR over 36 months, without losing the residential base that funds the rotation. The exact phasing, budget allocation, and KPI tree."}
  },
  commercial: {
    eyebrow: "Commercial integrators",
    headline: <>Your buyers don't Google "commercial security vendor." That's the <span className="accent">problem</span>, and the <span className="accent">opportunity.</span></>,
    sub: [
      "Commercial integration runs on specs, RFPs, GC relationships, and trade-show presence. Marketing here builds authority and named-account penetration, so you show up credible the moment a facility director, architect, or IT lead enters a buying cycle."
    ],
    painMirror: [
      "Your pipeline depends on the same six referral sources it has for the past decade. The trade-show calendar shapes the year. You last updated the website in 2019, and the cases on it are out of date. The reps who close are the ones with the rolodex; the new ones can't break in. Most of the time you tell yourself \"we don't really do marketing,\" and the project pipeline is lumpy because of it.",
      "The buyers you want aren't Googling \"commercial security vendor\": facility directors at multi-site retailers, regional property managers, IT leads at growing logistics operators. They're researching specific manufacturers and platforms, reading vertical case studies, watching demos, and asking peers. If you're not present in those research moments, you're not in the consideration set."
    ],
    callout: {
      title: "The cost of invisibility",
      body: [
        "Commercial integrators spending under 3% of revenue on marketing grow at industry trend pace, <strong>7 to 9% a year</strong>. Integrators spending 5 to 7% with proper ABM and content programs grow <strong>15 to 25%</strong>. The gap runs 50 to 100 basis points of EBITDA margin and a meaningful multiple difference at sale."
      ]
    },
    uniqueElement: {
      eyebrow: "Framework",
      title: "The Five-Layer Authority Stack.",
      lede: "Authority isn't built in one channel. It's built when a buyer encounters you in five different research contexts and finds the same credible operator each time.",
      render: (
        <div className="stack">
          {[
            {n:"Layer 05",name:"Strategic trade-show presence",d:"Booth strategy, pre-show ABM, post-show follow-up sequencing"},
            {n:"Layer 04",name:"Owner thought leadership",d:"Bylined articles, podcast presence, vertical conference speaking"},
            {n:"Layer 03",name:"Account-based outbound",d:"Multi-touch sequences into named target accounts"},
            {n:"Layer 02",name:"Vertical case studies",d:"Anchored to verticals you win in, not a generic case library"},
            {n:"Layer 01",name:"Specs-grade website",d:"Built for facility directors and IT leads, not for homeowners"}
          ].map((l,i)=>(
            <div className="stack-layer" key={i}>
              <span className="num">{l.n}</span>
              <span className="name">{l.name}</span>
              <span className="stack-desc">{l.d}</span>
            </div>
          ))}
        </div>
      )
    },
    caseStudy: {
      id: "case-studies/caprock-rentals",
      line: "Caprock Rentals rents jobsite surveillance trailers across Texas and grew on relationships. After we rebuilt its search foundation, the website started bringing in calls on its own.",
      cta: "Read the Caprock Rentals case study"
    },
    tiersTitle: "Three integrator tiers.",
    tiersLede: "Authority compounds. Pick the tier that fits where you are in the build.",
    tiers: [
      {name:"Foundation · Authority Build",price:"$3K – $4.5K",tag:"Specs-grade website plus the entity and answer foundation AI trusts.",bullets:["Specs-grade website rebuild","AI Visibility Audit: what AI tells a facility director or IT lead vetting vendors in your market","Entity foundation: Google Business Profile, consistent listings, and schema so AI reads you as one trusted integrator","Answer-ready content for commercial-intent questions, structured for AI Overviews and search","Vertical case study library (3-5 to start)","LinkedIn presence audit and rebuild","Tradeshow page + lead capture flow"],breakEven:"~1 signed contract per year"},
      {name:"Growth Engine · ABM + Content",price:"$6.5K – $9.5K",featured:true,tag:"Active named-account penetration, AI tracking, and ongoing content.",bullets:["Monthly AI visibility tracking for commercial-intent and competitor prompts","Account-based outbound, 200-account target lists","Answer-first vertical content program (4 assets / mo)","LinkedIn paid ABM","Sales enablement: decks, one-pagers, ROI calculators","Quarterly pipeline review with sales leadership"],breakEven:"~2 signed contracts per year"},
      {name:"Market Domination",price:"$11K – $17K",tag:"Full Authority Stack plus owner-level thought leadership.",bullets:["Owner thought-leadership program","National and vertical PR that gets you cited in AI answers","Competitor AI tracking across your verticals","Video case study program","Strategic trade-show enablement","BI dashboard tied to bid-to-close metrics","Dedicated strategist + creative team"],breakEven:"~4 signed contracts per year"}
    ],
    guide: {coverLabel:"Field Guide No. 03",coverTitle:"The Integrator's Authority Stack",no:"03",title:"The Integrator's Authority Stack",body:"Five layers of credibility that determine whether a facility director, architect, or IT lead takes your meeting. Built for integrators above $3M. Read it in 20 minutes."}
  }
};

export { HomePage, SegmentPage, ExitPage, AboutPage, GuidesPage, CaseStudyCaprock, FinalCTA, Footer, segmentData };
